import { useHost, computed, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useSharedState } from "@/runtime/composables/useSharedState";
import { createEventDispatcher } from "@/runtime/shared/events";
import { getNestedInput } from "components/Input/useInput";
import { getElementInternals } from "composables/useFormAssociated";
import { isValidNumberKey, clampNumberKeypress } from "composables/useKeyboardValidation";
import type { InputNumberProps } from "./inputNumber.types";
import { looseToNumber } from "@/runtime/utils";

export interface UseInputNumberOptions extends InputNumberProps {}

/**
 * Round a number to match the precision of the step value.
 * Prevents floating point errors like 10.049999999999999
 */
const roundToPrecision = (value: number, step: number): number => {
  const stepStr = step.toString();
  const decimalIndex = stepStr.indexOf(".");

  if (decimalIndex === -1) {
    return Math.round(value);
  }

  const decimals = stepStr.length - decimalIndex - 1;
  const multiplier = Math.pow(10, decimals);
  return Math.round(value * multiplier) / multiplier;
};

/** Clamp a value to min/max bounds. */
const clamp = (value: number, min?: number, max?: number): { value: number; clamped: boolean } => {
  if (min !== undefined && value < min) return { value: min, clamped: true };
  if (max !== undefined && value > max) return { value: max, clamped: true };
  return { value, clamped: false };
};

export const useInputNumber = (props: UseInputNumberOptions) => {
  const host = useHost();
  const state = useSharedState(props);
  const dispatchEvent = createEventDispatcher(host);

  const currentValue = computed((): number => {
    const value = state.modelValue?.value;
    if (value == null) return 0;
    const n = Number.parseFloat(String(value));
    return Number.isNaN(n) ? 0 : n;
  });

  const stepValue = (direction: 1 | -1, eventName: string) => {
    if (props.disabled) return;
    const step = props.step ?? 1;
    let newValue = roundToPrecision(currentValue.value + step * direction, step);
    newValue = clamp(newValue, props.min, props.max).value;
    dispatchEvent("update:modelValue", newValue);
    dispatchEvent(eventName, newValue);
  };

  const handleIncrement = () => stepValue(1, "increment");
  const handleDecrement = () => stepValue(-1, "decrement");

  const handleKeydown = (e: KeyboardEvent) => {
    if (!isValidNumberKey(e)) {
      e.preventDefault();
      return;
    }
    if (clampNumberKeypress(e, props.min, props.max)) {
      dispatchEvent("update:modelValue", Number.parseFloat((e.target as HTMLInputElement).value));
    }
  };

  const handleInputValueChange = (e: Event) => {
    const rawValue = (e as CustomEvent).detail[0];
    const { value, clamped } = clamp(looseToNumber(rawValue), props.min, props.max);
    const newValue = clamped ? value : rawValue;

    if (clamped) {
      const input = getNestedInput(host);
      if (input) input.value = String(newValue);
    }

    dispatchEvent("update:modelValue", newValue);
  };

  useInputNumberFormAssociation(host, () => state.modelValue?.value);
  useInputNumberListeners(host, handleInputValueChange, handleKeydown);

  onMounted(() => {
    if (host) (host as HTMLElement).focus = () => getNestedInput(host)?.focus();
  });

  return {
    state,
    handleIncrement,
    handleDecrement,
  };
};

/** Sync model value to ElementInternals so the host participates in FormData. */
const useInputNumberFormAssociation = (host: Element | null, getModelValue: () => unknown) => {
  const internals = ref<ElementInternals | null>(null);

  const syncFormValue = () => {
    if (!internals.value) return;
    const value = getModelValue();
    internals.value.setFormValue(value == null || value === "" ? null : String(value));
  };

  watch(getModelValue, syncFormValue);

  onMounted(() => {
    internals.value = getElementInternals(host as HTMLElement) ?? null;
    syncFormValue();
  });
};

/** Attach and cleanup event listeners on the inner e-input and native input. */
const useInputNumberListeners = (
  host: Element | null,
  onValueChange: (e: Event) => void,
  onKeydown: (e: KeyboardEvent) => void,
) => {
  const getElements = () => {
    const shadowRoot = (host as HTMLElement)?.shadowRoot;
    return {
      eInput: shadowRoot?.querySelector("e-input"),
      nativeInput: getNestedInput(host),
    };
  };

  onMounted(() => {
    const { eInput, nativeInput } = getElements();
    eInput?.addEventListener("update:modelValue", onValueChange);
    nativeInput?.addEventListener("keydown", onKeydown);
  });

  onBeforeUnmount(() => {
    const { eInput, nativeInput } = getElements();
    eInput?.removeEventListener("update:modelValue", onValueChange);
    nativeInput?.removeEventListener("keydown", onKeydown);
  });
};
